import styled from "@emotion/styled";
import { Box, H2, Stack, Text } from "atoms";
import { GenericImage } from "features/images";
import type { ContentData } from "../types";
import { DownloadButton } from "./DownloadButton";

type ResourceCardProps = {
  /** Horizontal card in header vs vertical card in grid */
  isFeatured?: boolean;
  content: ContentData;
};

type ResourceBodyProps = {
  content: ContentData;
};

function ResourceCard({ isFeatured = false, content }: ResourceCardProps) {
  return (
    <ContentBox isFeatured={isFeatured}>
      <CardMain isFeatured={isFeatured}>
        <ContentImage isFeatured={isFeatured}>
          <SGenericImage
            src={content.image}
            alt={content.image.altText ?? ""}
            sizes="(max-width: 1000px) 300px, 600px"
            isFeatured={isFeatured}
          />
        </ContentImage>

        <ContentTitle>
          <div>
            {isFeatured && <CategoryTag>Featured</CategoryTag>}
            {content.tags.map((tag) => (
              <CategoryTag key={tag}>{tag}</CategoryTag>
            ))}
          </div>

          <H2 size="md">{content.title}</H2>

          {isFeatured && <FeaturedContentBody content={content} />}
        </ContentTitle>

        {!isFeatured && <ContentBody content={content} />}
      </CardMain>
    </ContentBox>
  );
}
type WithIsFeatured = { isFeatured: boolean };

const ContentBox = styled(Box)<WithIsFeatured>(({ isFeatured, theme }) => ({
  display: "flex",
  overflow: "hidden",
  padding: 0,
  position: "relative",
  textAlign: "left",
  "&&": {
    color: theme.colors.fg,
  },
  ...(isFeatured
    ? {
        marginBottom: "2rem",
        [theme.breakpoints.xs]: {
          marginBottom: 0,
        },
      }
    : {
        fontSize: theme.fontSizes.xs,
      }),
}));

const CardMain = styled.div<WithIsFeatured>(({ theme, isFeatured }) => ({
  flex: 1,
  ...(isFeatured && {
    [theme.breakpoints.xs]: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "nowrap",
      alignItems: "center",
    },
  }),
}));

const ContentImage = styled.div<WithIsFeatured>(({ theme, isFeatured }) => ({
  position: "relative",

  ...(isFeatured
    ? {
        flex: "0 0 45%",
        [theme.breakpoints.xs]: {
          position: "relative",
          alignSelf: "stretch",
          img: {
            objectFit: "cover",
            width: "100%",
            height: "100%",
            display: "block",
          },
        },
        [theme.breakpoints.desktopMedium]: {
          flex: "0 0 50%",
        },
      }
    : {
        img: {
          aspectRatio: "1",
          display: "block",
        },
      }),
}));

const SGenericImage = styled(GenericImage)<WithIsFeatured>(({ isFeatured }) =>
  isFeatured
    ? {
        height: "100%",
      }
    : {},
);

const ContentTitle = styled(Stack)(({ theme }) => ({
  padding: "1rem 1rem 1rem",
  [theme.breakpoints.maxXs]: {
    display: "grid",
    alignContent: "center",
    flex: "1",
  },
}));

const Content = styled(Text)(({ theme }) => ({
  lineHeight: 1.3,
  margin: 0,
  flex: "0 0 100%",
  [theme.breakpoints.xs]: {
    flex: 1,
  },
}));

const CategoryTag = styled.div(({ theme }) => ({
  ...theme.fonts.display,
  backgroundColor: theme.colors.accent2LL,
  color: theme.colors.fg,
  textTransform: "uppercase",
  borderRadius: theme.borderRadius.sm,
  fontSize: 12,
  letterSpacing: "0.02em",
  lineHeight: 1,
  display: "inline-block",
  padding: ".5em 1em",
  marginRight: ".8ch",
}));

const FeaturedContentBody = styled(ContentBody)({
  padding: 0,
});

function ContentBody({ content, ...props }: ResourceBodyProps) {
  return (
    <Wrapper {...props}>
      {content.subtitle && <Content>{content.subtitle}</Content>}
      <DownloadButton slug={content.slug} url={content.download} />
    </Wrapper>
  );
}

const Wrapper = styled(Stack)({
  padding: "0 1rem 1rem 1rem",
});

export { ResourceCard };
